import React, { MouseEventHandler, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Flex } from 'components/common/styled/common-styled';
import { useIsMobile } from 'WindowDimensionProvider';
import {
  ResourceHeading2,
  ResourceLinkButton,
} from 'components/theme/Typography/ResourceTypography';
import { format } from 'date-fns';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { formatEventDateLong } from 'utils/dateTime';
import DatePickerButton from 'components/DatePickerButton';
import toMoment from 'utils/date/toMoment';
import { ButtonText } from 'components/theme/Button';
import minutesToIso from 'utils/duration/minutesToIso';
import { ReserveButton } from 'components/CollapsibleResource/styles';
import toLocaleDateString from 'utils/date/toLocaleDateString';
import PromotersCard from 'components/molecules/PromotersCard/PromotersCard';
import { Brand } from 'api/brand/fetchBrandById';
import { MediaV3, ListingPictureKind } from '@kouto/types';
import { BrandFlagContainer } from 'components/molecules/BrandFlagContainer';
import { getOriginalMediaLink } from 'utils/listings';
import useListingHref from 'hooks/useListingHref';
import { ResourceGroupDetail } from './ResourceGroupDetail';
import { NonMappedGroupSessionLoading } from '../pages/Skeleton';
import { ResourceGroupFullDetail } from './ResourceGroupFullDetail';

const buildReserveRoute = ({
  groupId,
  collectionId,
  date,
  time,
  duration,
}: {
  groupId: string;
  collectionId: string;
  date: string;
  time: string;
  duration: string;
}) => ({
  pathname: `/collection/${collectionId}/group/${groupId}`,
  search: `?${new URLSearchParams({ date, time, duration }).toString()}`,
});

interface NonMappedResourceGroupCardProps {
  bookingUnitLabel?: string;
  cancellationPolicyText?: string;
  capacityCount: number;
  capacityLabel: string;
  collectionId?: string | null;
  daySessionRangeFrom?: string;
  daySessionRangeTo?: string;
  description: string;
  detailHasMoreToShow?: boolean;
  displayDate?: Date;
  fromPrice: string;
  fromPriceUnit?: string;
  groupId: string;
  handleIsDayBlocked?: (day: moment.Moment) => boolean;
  hideCta?: boolean;
  resourceMedia: MediaV3[];
  isLoading?: boolean;
  newDatePickerOpen?: boolean;
  onMonthChange?: (date: Date) => void;
  onNewDatePickerDateChange?: (date: moment.Moment | null) => void;
  onNewDatePickerOpenChange?: (open: boolean) => void;
  onReserveClick?: MouseEventHandler<HTMLAnchorElement>;
  resourceGroupFullDetailDescription: string;
  resourceGroupFullDetailIncludedItems: string[];
  sessionDuration?: number;
  sessionTime?: string;
  title: string;
  unavailable?: boolean;
  vibes?: string[];
  collectionCustomCallToAction?: string | null;
  isSessionsLoading: boolean;
  promoterBrand: Brand | undefined;
  brandFlagName?: string;
}

export const NonMappedResourceGroupCard = ({
  bookingUnitLabel,
  cancellationPolicyText,
  capacityCount,
  capacityLabel,
  collectionId,
  daySessionRangeFrom,
  daySessionRangeTo,
  description,
  detailHasMoreToShow = false,
  displayDate,
  fromPrice,
  fromPriceUnit,
  groupId,
  handleIsDayBlocked,
  hideCta,
  resourceMedia,
  isLoading = false,
  newDatePickerOpen = false,
  onMonthChange,
  onNewDatePickerDateChange,
  onNewDatePickerOpenChange,
  onReserveClick,
  resourceGroupFullDetailDescription,
  resourceGroupFullDetailIncludedItems,
  sessionDuration,
  sessionTime,
  title,
  unavailable = false,
  vibes,
  collectionCustomCallToAction,
  isSessionsLoading,
  promoterBrand,
  brandFlagName,
}: NonMappedResourceGroupCardProps) => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const [showDetails, setShowDetails] = useState(false);
  const history = useHistory();

  // Build the destination as an internal route and let the router (whatever mode)
  // turn it into an href + navigate — never a hard-coded `#!` string.
  const reserveRoute = useMemo(() => {
    if (
      unavailable ||
      !sessionDuration ||
      !sessionTime ||
      !collectionId ||
      !displayDate
    ) {
      return null;
    }
    return buildReserveRoute({
      collectionId,
      date: toLocaleDateString(displayDate),
      duration: minutesToIso(sessionDuration),
      groupId,
      time: sessionTime,
    });
  }, [
    unavailable,
    sessionDuration,
    sessionTime,
    collectionId,
    displayDate,
    groupId,
  ]);

  const reserveHref = useListingHref(
    reserveRoute ?? { pathname: '', search: '' },
  );

  const handleReserveClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    onReserveClick?.(event);
    if (reserveRoute) {
      event.preventDefault();
      history.push(reserveRoute);
    }
  };

  const onReadMore = () => setShowDetails(true);
  const onClose = () => setShowDetails(false);

  const handleMonthChange = (momentDate: moment.Moment) => {
    onMonthChange?.(momentDate.toDate());
  };

  const isDisplayDateBlocked = displayDate
    ? handleIsDayBlocked?.(toMoment(displayDate))
    : false;

  const resourceImageMedia = resourceMedia.find(
    (media) => media.kind === ListingPictureKind.GALLERY,
  );
  const resourceImageUrl = resourceImageMedia
    ? getOriginalMediaLink(resourceImageMedia)
    : undefined;

  const datePickerButton =
    displayDate && !hideCta ? (
      <DatePickerButton
        date={isDisplayDateBlocked ? null : toMoment(displayDate)}
        initialVisibleMonth={toMoment(displayDate)}
        isDayBlocked={handleIsDayBlocked}
        isLoadingDates={isSessionsLoading}
        onDateChange={onNewDatePickerDateChange}
        onNextMonthClick={handleMonthChange}
        onOpenChange={onNewDatePickerOpenChange}
        onPrevMonthClick={handleMonthChange}
        open={newDatePickerOpen}
        renderButton={(props) => (
          <ButtonText {...props}>
            {formatEventDateLong(format(displayDate, 'yyyy-MM-dd'), locale)}
          </ButtonText>
        )}
      />
    ) : null;

  const reserveButton =
    displayDate && !hideCta ? (
      <ReserveButton
        aria-label={translate('reserveName', {
          name: title,
        })}
        as={unavailable ? 'div' : 'a'}
        className="non-mapped-session-reserve"
        data-testid={`reserve-${title}`}
        onClick={unavailable ? undefined : handleReserveClick}
        href={reserveRoute ? reserveHref : undefined}
      >
        {isLoading && translate('loading')}
        {unavailable && translate('unavailable')}
        {!isLoading &&
          !unavailable &&
          (collectionCustomCallToAction || translate('reserve'))}
      </ReserveButton>
    ) : null;

  return (
    <Wrapper className="summary-wrapper non-mapped-resource-group-card__mobile-wrapper">
      <ResourceImageContainer className="non-mapped-resource-group-card__resource-image-container">
        <BrandFlagContainer brandFlagName={brandFlagName}>
          <ResourceGroupImage
            alt={resourceImageMedia?.altText || title}
            src={resourceImageUrl}
          />
        </BrandFlagContainer>
      </ResourceImageContainer>
      {isMobile ? (
        <MobileContentWrapper>
          <Flex
            className="nonmapped-resource-group-card__mobile-content"
            direction="column"
            gap={6}
            alignItem="flex-start"
            justifyContent="space-between"
            width="100%"
          >
            <Flex
              direction="row"
              gap={6}
              alignItem="center"
              justifyContent="space-between"
              width="100%"
            >
              <ResourceHeading2
                className="nonmapped-resource-group-card__mobile-heading"
                fontSize="21px"
              >
                {title}
              </ResourceHeading2>

              <ResourceLinkButton
                className="nonmapped-resource-group-card__details-text"
                opacity="0.6"
                fontSize="12px"
                fontWeight="400"
                onClick={onReadMore}
              >
                {translate('viewDetails')}
              </ResourceLinkButton>
            </Flex>

            {!unavailable && (
              <Flex
                direction="row"
                gap={6}
                alignItem="center"
                justifyContent="space-between"
                width="100%"
              >
                <ResourceHeading2
                  className="nonmapped-resource-group-card__mobile-date"
                  fontSize="14px"
                  color="var(--way-palette-black-100)"
                  fontWeight="400"
                >
                  {datePickerButton}
                </ResourceHeading2>
                {fromPrice && (
                  <ResourceGroupText className="nonmapped-resource-group-card__details-text">
                    {translate('from')} <strong>{fromPrice}</strong>
                    {fromPriceUnit && ` ${fromPriceUnit}`}
                  </ResourceGroupText>
                )}
              </Flex>
            )}
            {!unavailable && bookingUnitLabel && (
              <ResourceGroupText className="nonmapped-resource-group-card__booking-unit">
                {bookingUnitLabel}
              </ResourceGroupText>
            )}
          </Flex>
          <Flex className="nonmapped-resource-group-card__reserve-button">
            {reserveButton}
          </Flex>
        </MobileContentWrapper>
      ) : (
        <DesktopContentWrapper>
          <Flex
            alignItem="flex-start"
            className="nonmapped-resource-group-card__content"
            direction="column"
            height="100%"
            justifyContent="space-between"
          >
            <Flex
              as="div"
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Flex
                as="div"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  width: '100%',
                  gap: '16px',
                }}
              >
                <ResourceHeading2
                  className="nonmapped-resource-group-card__heading"
                  fontSize="28px"
                  style={{
                    flex: '1 1 0',
                    minWidth: 0,
                    margin: 0,
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  {title}
                </ResourceHeading2>
                {promoterBrand?.name && (
                  <PromotersCard promoterBrand={promoterBrand} />
                )}
              </Flex>
              <ResourceGroupDetail
                description={description}
                maxLines={11}
                customStyles={resourceGroupTextStyles}
                onReadMore={onReadMore}
                hasMoreToShow={detailHasMoreToShow}
              />
            </Flex>
            <Flex direction="column" alignItem="flex-start" width="100%">
              <Flex direction="row" width="100%">
                {isLoading ? (
                  <NonMappedGroupSessionLoading />
                ) : (
                  <Flex direction="column" gap={4} alignItem="flex-start">
                    <ResourceGroupText className="non-mapped-session-date">
                      {!isMobile && datePickerButton}
                    </ResourceGroupText>
                    {daySessionRangeFrom && daySessionRangeTo && (
                      <ResourceGroupText className="non-mapped-session-time">
                        {daySessionRangeFrom} - {daySessionRangeTo}
                      </ResourceGroupText>
                    )}
                    {bookingUnitLabel && (
                      <ResourceGroupText className="non-mapped-session-booking-unit">
                        {bookingUnitLabel}
                      </ResourceGroupText>
                    )}
                  </Flex>
                )}
                <Flex direction="row" alignItem="center" gap={16}>
                  {!unavailable && (
                    <Flex direction="column" gap={4} alignItem="flex-end">
                      {isLoading ? (
                        <NonMappedGroupSessionLoading />
                      ) : (
                        fromPrice && (
                          <ResourceGroupText className="non-mapped-session-pricing">
                            {translate('from')} <strong>{fromPrice}</strong>
                            {fromPriceUnit && ` ${fromPriceUnit}`}
                          </ResourceGroupText>
                        )
                      )}
                      {!!capacityLabel && (
                        <ResourceGroupText className="non-mapped-session-capacity">
                          {capacityLabel}
                        </ResourceGroupText>
                      )}
                    </Flex>
                  )}

                  {reserveButton}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </DesktopContentWrapper>
      )}
      <ResourceGroupFullDetail
        capacityCount={capacityCount}
        customStyles={resourceGroupTextStyles}
        description={resourceGroupFullDetailDescription}
        includedItems={resourceGroupFullDetailIncludedItems}
        maxLines={300}
        onClose={onClose}
        showContent={showDetails}
        vibes={vibes}
        cancellationPolicyText={cancellationPolicyText}
        promoterBrand={promoterBrand}
      />
    </Wrapper>
  );
};

const Wrapper = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;

  @media (width > 768px) {
    flex-direction: row;
    gap: 0;
    align-items: stretch;
  }
`;

const MobileContentWrapper = styled.div`
  display: contents;
  width: 100%;
`;

const DesktopContentWrapper = styled.div`
  border-left: 0px;
  border: 0.5px solid var(--way-colors-borderColor);
  display: block;
  flex: 1;
  padding: 32px 36px 32px 48px;
  box-sizing: border-box;
`;

const ResourceImageContainer = styled.div`
  overflow: hidden;
  width: 100%;

  @media screen and (min-width: 768px) {
    aspect-ratio: 4 / 3;
    width: 574px;
  }
`;

export const ResourceGroupImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;

  @media screen and (max-width: 768px) {
    aspect-ratio: 16 / 9;
    height: 240px;
  }
`;

const resourceGroupTextStyles = css`
  margin-bottom: 0;
  & > p {
    font-family: ${({ theme }) => theme.font.primaryFont};
    margin: 0;
    padding: 0;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    font-size: 14px;
    color: var(--way-colors-contrastColorShades-80);
  }
`;

export const ResourceGroupText = styled.div`
  color: var(--way-colors-contrastColorShades-80);
  white-space: nowrap;
  font-size: 16px;
`;
